import { Capacitor } from '@capacitor/core';
import { NativeBiometric, BiometryType } from 'capacitor-native-biometric';

/**
 * Serviço de autenticação biométrica.
 *
 * Salva credenciais (email + password) no Keystore/Keychain nativo,
 * protegidas por biometria (digital/Face ID). Permite login sem digitar senha.
 *
 * Funciona apenas em plataformas nativas (Android/iOS).
 * No browser (PWA), todas as operações retornam gracefully sem efeito.
 */

const BIOMETRIC_SERVER = 'com.bolaocopadomundo26.app';
const BIOMETRIC_ENABLED_KEY = 'bolao_biometric_enabled';

export interface BiometricCredentials {
  email: string;
  password: string;
}

/**
 * Verifica se biometria está disponível no dispositivo.
 */
export async function isBiometricAvailable(): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) return false;

  try {
    const result = await NativeBiometric.isAvailable();
    return result.isAvailable && result.biometryType !== BiometryType.NONE;
  } catch {
    return false;
  }
}

/**
 * Retorna o tipo de biometria disponível (para exibir texto/ícone correto).
 */
export async function getBiometryType(): Promise<'fingerprint' | 'face' | 'none'> {
  if (!Capacitor.isNativePlatform()) return 'none';

  try {
    const result = await NativeBiometric.isAvailable();
    if (!result.isAvailable) return 'none';
    if (result.biometryType === BiometryType.FACE_AUTHENTICATION ||
        result.biometryType === BiometryType.FACE_ID) {
      return 'face';
    }
    return 'fingerprint';
  } catch {
    return 'none';
  }
}

/**
 * Salva credenciais protegidas por biometria.
 * Chamado após login bem-sucedido quando o usuário aceita usar biometria.
 */
export async function saveCredentials(credentials: BiometricCredentials): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) return false;

  try {
    await NativeBiometric.setCredentials({
      username: credentials.email,
      password: credentials.password,
      server: BIOMETRIC_SERVER,
    });
    localStorage.setItem(BIOMETRIC_ENABLED_KEY, 'true');
    return true;
  } catch {
    return false;
  }
}

/**
 * Recupera credenciais após verificação biométrica.
 * Mostra o prompt de biometria ao usuário.
 */
export async function getCredentialsWithBiometric(): Promise<BiometricCredentials | null> {
  if (!Capacitor.isNativePlatform()) return null;

  try {
    // Verificar biometria do usuário
    await NativeBiometric.verifyIdentity({
      reason: 'Confirme sua identidade para entrar',
      title: 'Login Biométrico',
      subtitle: 'Use sua digital ou Face ID',
      description: 'Bolão da Copa 2026',
    });

    // Se verificação passou, recuperar credenciais
    const credentials = await NativeBiometric.getCredentials({
      server: BIOMETRIC_SERVER,
    });

    return {
      email: credentials.username,
      password: credentials.password,
    };
  } catch {
    return null;
  }
}

/**
 * Remove credenciais salvas (logout ou desativar biometria).
 */
export async function clearBiometricCredentials(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;

  try {
    await NativeBiometric.deleteCredentials({ server: BIOMETRIC_SERVER });
  } catch {
    // Ignora se não existiam
  }
  localStorage.removeItem(BIOMETRIC_ENABLED_KEY);
}

/**
 * Verifica se o usuário já ativou login biométrico.
 */
export function isBiometricEnabled(): boolean {
  return localStorage.getItem(BIOMETRIC_ENABLED_KEY) === 'true';
}

/**
 * Desativa biometria sem apagar credenciais (toggle off).
 */
export function disableBiometric(): void {
  localStorage.removeItem(BIOMETRIC_ENABLED_KEY);
}
